import { Divider, Menu, MenuItem } from '@mui/material';
import React from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';

interface SupportPolicyMenuProps {
  hasAccessToManageSupportPolicies: boolean;
  hasAccessToManageEscalationPolicies: boolean;
  menuAnchorEl: HTMLElement | null;
  setMenuAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  menuPolicyId: string | null;
  setMenuPolicyId: React.Dispatch<React.SetStateAction<string | null>>;
  supportPolicies: NormalizedSupportPolicy[];
  handleUnpin: (policy: NormalizedSupportPolicy) => void;
  handlePin: (policy: NormalizedSupportPolicy) => void;
  handleArchive: (policy: NormalizedSupportPolicy) => void;
  handleUnarchive: (policy: NormalizedSupportPolicy) => void;
  handleDelete: (id: string | null) => void;
  onEditPolicy: (policy: NormalizedSupportPolicy) => void;
}

export const SupportPolicyMenu = ({
  hasAccessToManageSupportPolicies,
  hasAccessToManageEscalationPolicies,
  menuAnchorEl,
  setMenuAnchorEl,
  menuPolicyId,
  setMenuPolicyId,
  supportPolicies,
  handleUnpin,
  handlePin,
  handleArchive,
  handleUnarchive,
  handleDelete,
  onEditPolicy,
}: SupportPolicyMenuProps) => {
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuPolicyId(null);
  };

  if (!hasAccessToManageSupportPolicies) return null;

  const policy = menuPolicyId ? supportPolicies.find((p) => p.id === menuPolicyId) : null;
  if (!policy) return null;

  const isEscalation = policy.category === POLICY_CATEGORY_ESCALATION;
  const items: React.ReactNode[] = [];

  if (!policy.archivedAt && (!isEscalation || hasAccessToManageEscalationPolicies)) {
    items.push(
      <MenuItem
        key="edit"
        data-testid="policy-menu-edit"
        onClick={() => {
          onEditPolicy(policy);
          handleMenuClose();
        }}
      >
        <DynamicIcon name="edit" style={{ marginRight: 1 }} /> Edit
      </MenuItem>,
    );
  }

  if (!policy.archivedAt && !isEscalation) {
    if (policy.pinnedAt) {
      items.push(
        <MenuItem key="unpin" onClick={() => handleUnpin(policy)}>
          <DynamicIcon name="push_pin" style={{ marginRight: 1 }} /> Unpin
        </MenuItem>,
      );
    } else {
      items.push(
        <MenuItem key="pin" onClick={() => handlePin(policy)}>
          <DynamicIcon name="push_pin" style={{ marginRight: 1 }} /> Pin
        </MenuItem>,
      );
    }
  }

  if (!isEscalation) {
    if (policy.archivedAt) {
      items.push(
        <MenuItem key="unarchive" onClick={() => handleUnarchive(policy)}>
          <DynamicIcon name="unarchive" style={{ marginRight: 1 }} /> Unarchive
        </MenuItem>,
      );
    } else {
      items.push(
        <MenuItem key="archive" onClick={() => handleArchive(policy)}>
          <DynamicIcon name="archive" style={{ marginRight: 1 }} /> Archive
        </MenuItem>,
      );
    }

    items.push(<Divider key="divider" />);

    items.push(
      <MenuItem
        key="delete"
        onClick={() => handleDelete(policy.id)}
        sx={{ color: 'error.main' }}
      >
        <DynamicIcon name="delete" style={{ marginRight: 1, color: 'inherit' }} /> Delete
      </MenuItem>,
    );
  }

  return (
    <Menu
      anchorEl={menuAnchorEl}
      open={Boolean(menuAnchorEl)}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
    >
      {items}
    </Menu>
  );
};
